import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnDestroy, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { UiAtomButtonComponent } from '../../atoms/ui-atom-button/ui-atom-button.component';
import { UiAtomTextComponent } from '../../atoms/ui-atom-text/ui-atom-text.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { UiMoleculeDialogDeleteWithConfirmComponent } from '../ui-molecule-dialog-delete-with-confirm/ui-molecule-dialog-delete-with-confirm.component';
import { SurveyModule } from 'survey-angular-ui';
import { Model } from 'survey-core';
import 'survey-core/survey-core.min.css';
import { FormService } from '@core/services/use-cases/form.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import { ResponsePdfExportService } from '@core/services/utils/response-pdf-export.service';
import { FormResponsesExportService } from '@core/services/utils/form-responses-export.service';
import { DialogExportColumnsService } from '@shared/services/dialog-export-columns.service';

export interface FormResponseData {
  title: string;
  formName: string;
  schema: Record<string, unknown>;
  existingResponse?: Record<string, unknown>;
  mode: 'view' | 'respond';
  beneficiaryId?: string;
  formId: string;
  /** Nombre del beneficiario, para el diálogo de privacidad del PDF. */
  beneficiaryFirstName?: string;
  beneficiaryLastName?: string;
}

export interface FormResponseResult {
  submitted: boolean;
  saved?: boolean;
  response?: Record<string, unknown>;
  formId: string;
  beneficiaryId?: string;
}

@Component({
  selector: 'ui-molecule-dialog-form-response',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    UiAtomButtonComponent,
    UiAtomTextComponent,
    TranslatePipe,
    SurveyModule,
  ],
  templateUrl: './ui-molecule-dialog-form-response.component.html',
  styleUrl: './ui-molecule-dialog-form-response.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class FormResponseDialogComponent implements AfterViewInit, OnDestroy {
  surveyModel!: Model;
  isLoading = false;
  isViewMode = false;
  isSaving = false;
  isSubmitting = false;
  hasSaved = false;
  isExpanded = false;
  isDownloadingPdf = false;
  isCompleted = false; // Tras submit OK: se muestra la Thank you page dentro del diálogo

  private lastSavedSnapshot = '{}';
  private completedResult: FormResponseResult | null = null;
  private readonly destroy$ = new Subject<void>();

  // Idioma del contenido del formulario (independiente del idioma de la app).
  // Solo se muestra el selector si el schema tiene más de un locale traducido.
  availableLocales: { code: string; label: string }[] = [];
  selectedLocale = 'en';

  private readonly localeNativeNames: Record<string, string> = {
    en: 'English',
    uk: 'Українська',
    hu: 'Magyar',
    sk: 'Slovenčina',
  };

  constructor(
    public dialogRef: MatDialogRef<FormResponseDialogComponent, FormResponseResult>,
    @Inject(MAT_DIALOG_DATA) public data: FormResponseData,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
    private dialog: MatDialog,
    private formService: FormService,
    private snackbar: SnackbarService,
    private responsePdfExport: ResponsePdfExportService,
    private responsesExport: FormResponsesExportService,
    private dialogExportColumns: DialogExportColumnsService,
  ) {
    this.isViewMode = data.mode === 'view';
  }

  ngAfterViewInit(): void {
    this.surveyModel = new Model(this.data.schema);

    // Por defecto siempre English, aunque ya sea el default de SurveyJS.
    this.selectedLocale = 'en';
    this.surveyModel.locale = this.selectedLocale;
    this.availableLocales = this.surveyModel.getUsedLocales().map((code) => ({
      code,
      label: this.localeNativeNames[code] ?? code,
    }));

    if (this.data.existingResponse) {
      this.surveyModel.data = this.data.existingResponse;
      this.hasSaved = true;
    }

    this.lastSavedSnapshot = JSON.stringify(this.surveyModel.data || {});

    if (this.isViewMode) {
      this.surveyModel.mode = 'display';
    } else {
      this.surveyModel.mode = 'edit';
      this.surveyModel.showCompleteButton = false;
      this.surveyModel.showNavigationButtons = true;
    }

    this.dialogRef
      .backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.attemptClose());

    this.dialogRef
      .keydownEvents()
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event.key === 'Escape'),
      )
      .subscribe(() => this.attemptClose());

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onLocaleChange(code: string): void {
    this.selectedLocale = code;
    if (this.surveyModel) {
      this.surveyModel.locale = code;
    }
  }

  onSave(): void {
    if (!this.surveyModel || !this.data.beneficiaryId || !this.data.formId) return;
    if (this.isSaving || this.isSubmitting) return;

    this.isSaving = true;
    const answers = this.surveyModel.data;

    this.formService.saveFormResponse(this.data.beneficiaryId, this.data.formId, answers).subscribe({
      next: () => {
        this.isSaving = false;
        this.hasSaved = true;
        this.lastSavedSnapshot = JSON.stringify(answers || {});
        this.snackbar.open(
          this.translateService.instant('forms.response.messages.saveSuccess')
        );
      },
      error: (err) => {
        this.isSaving = false;
        this.snackbar.notifyHttpError(err);
      },
    });
  }

  onSubmit(): void {
    if (!this.surveyModel || !this.data.beneficiaryId || !this.data.formId) return;
    if (this.isSaving || this.isSubmitting) return;

    const hasErrors = this.surveyModel.hasErrors(true, true);
    if (hasErrors) return;

    const confirmDialogRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      width: '480px',
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        titleKey: 'publicForm.fill.confirmSubmit.title',
        messageKey: 'publicForm.fill.confirmSubmit.message',
        checkboxLabelKey: 'publicForm.fill.confirmSubmit.checkbox',
        confirmButtonKey: 'publicForm.fill.buttons.submit',
        cancelButtonKey: 'buttons.cancel',
      },
    });

    confirmDialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      const beneficiaryId = this.data.beneficiaryId!;
      const formId = this.data.formId;
      const answers = this.surveyModel.data;

      this.isSubmitting = true;

      this.formService
        .saveFormResponse(beneficiaryId, formId, answers)
        .pipe(switchMap(() => this.formService.submitFormResponse(beneficiaryId, formId)))
        .subscribe({
          next: () => {
            this.isSubmitting = false;
            this.lastSavedSnapshot = JSON.stringify(answers || {});
            // En lugar de cerrar, mostramos la Thank you page dentro del diálogo.
            // doComplete() transiciona el modelo a 'completed' y renderiza
            // completedHtml / completedHtmlOnCondition (incluye el HTML de scoring
            // si lo tiene) o el texto por defecto de SurveyJS. Ya validamos con hasErrors().
            this.completedResult = { submitted: true, response: answers, formId, beneficiaryId };
            this.isCompleted = true;
            this.surveyModel.mode = 'display';
            this.surveyModel.doComplete();
            this.snackbar.open(
              this.translateService.instant('forms.response.messages.submitSuccess')
            );
            this.cdr.detectChanges();
          },
          error: (err) => {
            this.isSubmitting = false;
            this.snackbar.notifyHttpError(err);
          },
        });
    });
  }

  attemptClose(): void {
    if (this.isSaving || this.isSubmitting) return;

    // Si ya está completado (Thank you page), cerrar devolviendo el resultado del submit.
    if (this.isCompleted) {
      this.closeDialog();
      return;
    }

    if (!this.hasUnsavedChanges()) {
      this.closeDialog();
      return;
    }

    const confirmRef = this.dialog.open(UiMoleculeDialogDeleteWithConfirmComponent, {
      width: '480px',
      disableClose: true,
      autoFocus: false,
      panelClass: ['dialog-sm', 'dialog-safe-area'],
      data: {
        titleKey: 'dialogs.unsavedChanges.title',
        messageKey: 'dialogs.unsavedChanges.message',
        confirmButtonKey: 'dialogs.unsavedChanges.confirm',
        cancelButtonKey: 'buttons.cancel',
      },
    });

    confirmRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) this.closeDialog();
    });
  }

  /** Solo ofrecemos PDF en modo view sobre un cuestionario ya respondido. */
  get canDownloadPdf(): boolean {
    return this.isViewMode && !!this.data.existingResponse;
  }

  onDownloadPdf(): void {
    if (this.isDownloadingPdf || !this.canDownloadPdf) return;

    // Sin datos de beneficiario no hay nada que anonimizar: descarga directa.
    if (!this.data.beneficiaryFirstName && !this.data.beneficiaryLastName) {
      this.generatePdf([]);
      return;
    }

    const nameColumns = this.responsesExport
      .getBeneficiaryExportColumns()
      .filter((c) => c.key === 'firstName' || c.key === 'lastName');

    this.dialogExportColumns
      .open(nameColumns, {
        allowEmpty: true,
        titleKey: 'forms.response.pdf.privacyDialog.title',
        subtitleKey: 'forms.response.pdf.privacyDialog.subtitle',
        emptyHintKey: 'forms.response.pdf.privacyDialog.emptyHint',
        noteKey: 'forms.response.pdf.privacyDialog.scoringNote',
      })
      .subscribe((selectedKeys) => {
        if (!selectedKeys) return; // cancelado
        this.generatePdf(selectedKeys);
      });
  }

  private generatePdf(selectedKeys: string[]): void {
    this.isDownloadingPdf = true;

    const { fileName, subtitle } = this.responsePdfExport.buildPdfNaming(
      this.data.formName,
      [
        selectedKeys.includes('firstName') ? this.data.beneficiaryFirstName : undefined,
        selectedKeys.includes('lastName') ? this.data.beneficiaryLastName : undefined,
      ],
      this.data.beneficiaryId,
    );

    this.responsePdfExport
      .downloadResponsePdf({
        formName: this.data.formName,
        subtitle,
        schema: this.data.schema,
        answers: this.data.existingResponse ?? {},
        fileName,
      })
      .catch(() => {
        this.snackbar.open(
          this.translateService.instant('forms.response.messages.pdfError')
        );
      })
      .finally(() => {
        this.isDownloadingPdf = false;
        this.cdr.markForCheck();
      });
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      this.dialogRef.updateSize('100vw', '100vh');
      this.dialogRef.addPanelClass('dialog-expanded');
      this.dialogRef.removePanelClass('dialog-safe-area');
    } else {
      this.dialogRef.updateSize('90vw', '90vh');
      this.dialogRef.removePanelClass('dialog-expanded');
      this.dialogRef.addPanelClass('dialog-safe-area');
    }
  }

  private hasUnsavedChanges(): boolean {
    if (!this.surveyModel || this.isViewMode) return false;
    return JSON.stringify(this.surveyModel.data || {}) !== this.lastSavedSnapshot;
  }

  private closeDialog(): void {
    if (this.completedResult) {
      this.dialogRef.close(this.completedResult);
      return;
    }
    this.dialogRef.close({
      submitted: false,
      formId: this.data.formId,
      beneficiaryId: this.data.beneficiaryId,
    });
  }
}
